import { useEffect, useRef } from "react";
import { api } from "./api";

/**
 * Field GPS reporter (ISOLATED / opt-in live-GPS spike).
 *
 * This hook is the client half of the spike described in migration
 * 232_tech_location_pings.sql. It is deliberately conservative:
 *
 *   • OFF by default. It only does anything when the localStorage flag
 *     `field.gpsEnabled` is truthy ("1" / "true"). There is no settings UI —
 *     flip it from the console with:
 *         localStorage.setItem("field.gpsEnabled", "1")
 *     (and remove it to disable). The mounting component additionally guards
 *     on technician-role + field shell, so it can never run for office users.
 *
 *   • Battery-aware. Normal cadence is ~30s. If the Battery Status API reports
 *     a low level (<20%) or "not charging", the cadence backs off to 2–5min.
 *
 *   • Movement-gated. A new fix within ~25m of the last SENT fix is skipped
 *     (no POST) to save battery and DB churn — unless a heartbeat interval has
 *     elapsed, so a parked tech still refreshes their "GPS Nm ago" age.
 *
 *   • Fails silent. Permission denial, missing geolocation, or a failed POST
 *     never throw into the app; the reporter just stops or skips.
 */

export const GPS_ENABLED_FLAG = "field.gpsEnabled";

const NORMAL_INTERVAL_MS = 30_000;
const LOW_BATTERY_INTERVAL_MS = 180_000; // 3 min
const MOVEMENT_THRESHOLD_M = 25;
// Even when stationary, refresh at least this often so the age label stays live.
const HEARTBEAT_MS = 5 * 60_000;
const LOW_BATTERY_THRESHOLD = 0.2;

interface BatteryLike {
    level: number;
    charging: boolean;
    addEventListener?: (type: string, listener: () => void) => void;
    removeEventListener?: (type: string, listener: () => void) => void;
}

function getBattery(): Promise<BatteryLike | null> {
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryLike> };
    if (typeof nav.getBattery !== "function") return Promise.resolve(null);
    return nav.getBattery().catch(() => null);
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function flagEnabled(): boolean {
    try {
        const v = localStorage.getItem(GPS_ENABLED_FLAG);
        return v === "1" || v === "true";
    } catch {
        return false;
    }
}

/**
 * Starts the opt-in field GPS reporter while `enabled` is true. Returns
 * nothing — it's a side-effect hook. The caller (the mount component) is
 * responsible for the role/shell guard; this hook adds the localStorage gate.
 */
export function useTechLocationReporter(enabled: boolean): void {
    const lastSent = useRef<{ lat: number; lng: number; at: number } | null>(null);

    useEffect(() => {
        if (!enabled) return;
        if (!flagEnabled()) return;
        if (typeof navigator === "undefined" || !("geolocation" in navigator)) return;

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let battery: BatteryLike | null = null;

        const currentIntervalMs = (): number => {
            if (battery && (battery.level < LOW_BATTERY_THRESHOLD || !battery.charging)) {
                return LOW_BATTERY_INTERVAL_MS;
            }
            return NORMAL_INTERVAL_MS;
        };

        const sendPing = (pos: GeolocationPosition) => {
            const { latitude, longitude, accuracy } = pos.coords;
            const prev = lastSent.current;
            const now = Date.now();
            // Movement gate: skip near-duplicate fixes unless the heartbeat
            // window has elapsed (so a parked tech still refreshes their age).
            if (prev) {
                const moved = haversineMeters(prev.lat, prev.lng, latitude, longitude);
                if (moved < MOVEMENT_THRESHOLD_M && now - prev.at < HEARTBEAT_MS) return;
            }
            const batteryLevel =
                battery && Number.isFinite(battery.level)
                    ? Math.round(Math.min(1, Math.max(0, battery.level)) * 100)
                    : undefined;
            void api
                .post("/api/tech/location/ping", {
                    latitude,
                    longitude,
                    accuracyM: Number.isFinite(accuracy) ? accuracy : undefined,
                    batteryLevel,
                })
                .then(() => {
                    lastSent.current = { lat: latitude, lng: longitude, at: now };
                })
                .catch(() => {
                    /* network/server hiccup — silently retry next tick */
                });
        };

        const tick = () => {
            if (cancelled) return;
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    if (cancelled) return;
                    sendPing(pos);
                    timer = setTimeout(tick, currentIntervalMs());
                },
                (err) => {
                    if (cancelled) return;
                    // Permission denied → disable silently and stop the loop.
                    if (err.code === err.PERMISSION_DENIED) return;
                    // Transient error (timeout / position unavailable) → retry.
                    timer = setTimeout(tick, currentIntervalMs());
                },
                { enableHighAccuracy: false, maximumAge: 15_000, timeout: 20_000 },
            );
        };

        void getBattery().then((b) => {
            if (cancelled) return;
            battery = b;
            tick();
        });

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [enabled]);
}
